import { useDispatch, useSelector } from 'react-redux';

import { OwnerParams } from 'shared/types';
import { Visit, VisitState } from './types';
import { fetchVisit } from 'store/visits/actions';
import { getConfirmationVisit, getInvalidationToken, getVisitStateById } from './selectors';
import { useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';

export const useVisitState = (visitId: string, shouldReturnFromCache = false): VisitState => {
  const { ownerId } = useParams<OwnerParams>();
  const dispatch = useDispatch();
  const visitState = useSelector(getVisitStateById(visitId));
  const invalidationToken = useSelector(getInvalidationToken);

  useEffect(() => {
    if (!shouldReturnFromCache && visitId) {
      dispatch(fetchVisit.request({ buildingId: ownerId, visitId }));
    }
  }, [shouldReturnFromCache, visitId, ownerId, dispatch]);

  // The store is keyed by visitId ALONE (`mappedData[visitId]`), so every owner shares one entry.
  // This records which (owner, visit) the guard's error actually belongs to — without it a failure
  // under one owner would suppress the fetch under the next, including the ordinary case of a route
  // param resolving late and changing `ownerId` beneath a fixed visitId.
  const attemptedFor = useRef<string | null>(null);

  useEffect(() => {
    if (!shouldReturnFromCache || !visitId || visitState.data || visitState.loading) {
      return;
    }

    const target = `${ownerId ?? ''}::${visitId}`;
    // `visitState` is this effect's own dependency, so it re-runs on every change to it — including
    // the failure it just caused. Failure leaves `data` null and `loading` false, which satisfies
    // everything above, and `fetchVisit.request` spreads the previous state so the error survives the
    // retry: an endless request loop under a stuck error screen.
    //
    // So a failure is terminal — but only for the target that produced it. A different owner or visit
    // has not been tried yet and gets its own attempt.
    if (visitState.error && attemptedFor.current === target) {
      return;
    }

    attemptedFor.current = target;
    dispatch(fetchVisit.request({ buildingId: ownerId, visitId }));
  }, [shouldReturnFromCache, visitState, visitId, ownerId, dispatch]);

  /*
   * The second subscriber to the activity-stream invalidation, alongside the two list hooks.
   *
   * Neither effect above re-reads a visit that is already cached: the `shouldReturnFromCache` one
   * bails on `visitState.data`, and the other only re-runs when `visitId`/`ownerId` change. So an open
   * visit detail never noticed a change made anywhere else. It looked like it worked, because the list
   * refresh writes every visit it returns into `mappedData` — but only for visits inside the list's
   * date window, which defaults to TODAY. Deep-link to a visit on another date (the canonical entry to
   * this route), have it cancelled elsewhere, and the pane kept showing "Visit expected" with a live
   * Cancel button on an already-cancelled visit, indefinitely. Confirmed against a live tenant.
   *
   * Fires only on a CHANGE to the token, never on mount — the effects above already own the first read,
   * and the ref is compared before anything else so a re-render cannot replay it. Skipped while a read
   * is already in flight, so a frame landing mid-fetch doesn't stack a second one.
   *
   * `attemptedFor` is deliberately left alone: a visit whose fetch has already failed stays terminal,
   * exactly as it is for the effect above, rather than a stream of frames retrying it forever.
   */
  const prevInvalidationToken = useRef(invalidationToken);
  useEffect(() => {
    if (prevInvalidationToken.current === invalidationToken) {
      return;
    }
    prevInvalidationToken.current = invalidationToken;

    if (!visitId || visitState.loading) {
      return;
    }

    dispatch(fetchVisit.request({ buildingId: ownerId, visitId }));
  }, [invalidationToken, visitId, ownerId, visitState.loading, dispatch]);

  return visitState;
};

export const useConfirmationVisit = (visitId: string): Visit | null => {
  return useSelector(getConfirmationVisit(visitId));
};
